import { Button } from "@scribenote/brian-design-system";
import { formatCountdown } from "@utils/format-date";
import { type KeyboardEvent, useEffect, useMemo, useRef } from "react";

import { parseMs } from "./monitorTime";
import type { RoomStill } from "./perceptionClient";
import { DEFAULT_ASPECT, StillFrame } from "./StillFrame";

const secondsBefore = (still: RoomStill, newest: RoomStill) =>
  Math.round(
    ((parseMs(newest.capturedAt) ?? 0) - (parseMs(still.capturedAt) ?? 0)) /
      1000,
  );

const timeLabel = (still: RoomStill, newest: RoomStill) => {
  const seconds = secondsBefore(still, newest);
  return seconds === 0 ? "Latest" : `\u2212${formatCountdown(seconds)}`;
};

const PRELOAD_NEIGHBOURS = 2;
const JUMP = 10;

/**
 * A media-player scrubber over a room's recent stills, oldest first: the
 * current still large, a slider with the time before the newest, a thumbnail
 * strip, and ←/→ to step from anywhere inside it.
 */
export const StillScrubber = <S extends RoomStill>({
  windowLabel,
  stills,
  index,
  onIndex,
  chosen,
  full,
  onChoose,
}: {
  /** What the stills span, as "last two minutes". */
  windowLabel: string;
  stills: readonly S[];
  index: number;
  onIndex: (index: number) => void;
  chosen: boolean;
  full: boolean;
  onChoose: (still: S) => void;
}) => {
  const current = stills[index];
  const newest = stills[stills.length - 1];
  const strip = useRef<HTMLDivElement>(null);
  const labels = useMemo(
    () => (newest ? stills.map((still) => timeLabel(still, newest)) : []),
    [stills, newest],
  );

  useEffect(() => {
    // Only the strip scrolls: `scrollIntoView` would also scroll the dialog.
    const thumb = strip.current?.children[index];
    if (strip.current && thumb instanceof HTMLElement)
      strip.current.scrollLeft =
        thumb.offsetLeft -
        strip.current.offsetLeft -
        (strip.current.clientWidth - thumb.offsetWidth) / 2;

    for (
      let i = Math.max(index - PRELOAD_NEIGHBOURS, 0);
      i <= index + PRELOAD_NEIGHBOURS;
      i++
    ) {
      const near = stills[i];
      if (!near) continue;
      const preload = new Image();
      preload.referrerPolicy = "no-referrer";
      preload.src = near.url;
    }
  }, [stills, index]);

  if (!current || !newest) return null;

  const step = (delta: number) =>
    onIndex(Math.min(Math.max(index + delta, 0), stills.length - 1));
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    step((event.key === "ArrowLeft" ? -1 : 1) * (event.shiftKey ? JUMP : 1));
  };

  return (
    <div className="flex flex-col gap-2" onKeyDown={onKeyDown}>
      <StillFrame url={current.url} />
      <div className="flex items-center gap-3">
        <input
          type="range"
          aria-label={`Time in the ${windowLabel}`}
          aria-valuetext={labels[index]}
          min={0}
          max={stills.length - 1}
          step={1}
          value={index}
          onChange={(event) => onIndex(Number(event.target.value))}
          className="min-w-0 flex-1 accent-ink-brian"
        />
        <span className="w-16 shrink-0 text-right font-mono text-sm text-ink">
          {labels[index]}
        </span>
        <Button
          variant="accent"
          size="sm"
          disabled={chosen || full}
          onClick={() => onChoose(current)}
        >
          {chosen ? "Chosen" : "Use this still"}
        </Button>
      </div>
      <div
        ref={strip}
        data-dd-privacy="hidden"
        className="flex gap-1 overflow-x-auto pb-1"
      >
        {stills.map((still, i) => (
          <button
            key={still.reference}
            type="button"
            aria-label={`Still ${labels[i]}`}
            aria-current={i === index}
            onClick={() => onIndex(i)}
            style={{
              aspectRatio:
                still.width && still.height
                  ? still.width / still.height
                  : DEFAULT_ASPECT,
            }}
            className={
              i === index
                ? "h-14 shrink-0 rounded-paper outline-2 outline-offset-1 outline-accent"
                : "h-14 shrink-0 rounded-paper opacity-70 hover:opacity-100"
            }
          >
            <img
              src={still.url}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              className="h-full w-full rounded-paper object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
};
